/**
 * <fitment-search>: Cascading Hersteller / Hubraum / Modell / Baujahr selector.
 *
 * Daten:
 *   - vehicles.json (data-vehicles-url) — Liste aller Fahrzeuge
 *
 * Auswahl wird in localStorage gespeichert (Key: 'tsstec.vehicle').
 * Beim Submit wird ein CustomEvent 'tsstec:vehicle:changed' am document gefired
 * und auf /collections/passende-teile?filter.p.m.custom.fits_vehicles=<handle>
 * weitergeleitet — Shopify filtert serverseitig, kein JSON-Index mehr.
 *
 * Rendert eine Chip-Reihe `[data-fitment-chips]` mit den aktuell gewählten Werten,
 * jedes Chip mit ✕ entfernt nur seinen Cascade-Schritt.
 *
 * Submit-Button: `[data-fitment-submit]` — bekommt einen Treffer-Counter "(N)" angehängt,
 * sobald ein konkretes Fahrzeug gewählt ist.
 */
(function () {
  const STORAGE_KEY = 'tsstec.vehicle';

  // Marken aus JTL kommen meist in CAPS — hier auf Title-Case normalisieren,
  // aber bekannte Acronyme/Schreibweisen erhalten.
  const BRAND_KEEP_CAPS = new Set(['BMW', 'KTM', 'SYM', 'MV', 'GASGAS', 'CF']);
  function formatBrand(name) {
    if (!name) return '';
    return name.split(/\s+/).map((word) => {
      if (BRAND_KEEP_CAPS.has(word.toUpperCase())) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  }

  function displacementOrder(d) {
    if (!d) return 999;
    if (/^bis\s/i.test(d)) {
      const m = d.match(/(\d+)/); return m ? parseInt(m[1], 10) - 1000 : -999;
    }
    if (/^über\s/i.test(d) || /^ueber\s/i.test(d)) {
      const m = d.match(/(\d+)/); return m ? parseInt(m[1], 10) + 10000 : 99999;
    }
    const m = d.match(/(\d+)\s*-\s*(\d+)/);
    if (m) return parseInt(m[1], 10);
    return 999;
  }

  class FitmentSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-fitment-form]');
      this.brandSelect = this.querySelector('[data-fitment-brand]');
      this.dispSelect = this.querySelector('[data-fitment-displacement]');
      this.modelSelect = this.querySelector('[data-fitment-model]');
      this.yearSelect = this.querySelector('[data-fitment-year]');
      this.submitButton = this.querySelector('[data-fitment-submit]');
      this.submitLabel = this.submitButton ? (this.submitButton.dataset.label || this.submitButton.textContent.trim()) : 'Passende Artikel finden';
      this.resetButton = this.querySelector('[data-fitment-reset]');
      this.statusBox = this.querySelector('[data-fitment-status]');
      this.chipsBox = this.querySelector('[data-fitment-chips]');

      this.setStatus('');

      this.loadVehicles()
        .then(() => {
          if (!this.brands.length) {
            this.setStatus('Keine Fahrzeugdaten gefunden.', 'error');
            return;
          }
          this.populateBrands();
          this.attachListeners();
          this.restoreFromStorage();
          this.renderChips();
          this.setStatus('');
        })
        .catch((err) => {
          console.error('[fitment-search] Vehicle-Daten konnten nicht geladen werden:', err);
          this.setStatus('Fitment-Daten konnten nicht geladen werden: ' + err.message, 'error');
        });
    }

    async loadVehicles() {
      const url = this.dataset.vehiclesUrl;
      if (!url) throw new Error('data-vehicles-url fehlt');
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status} bei ${url}`);
      const data = await res.json();
      this.vehicles = data.vehicles || [];
      this.brands = data.brands || [];
    }

    /* Live-Trefferzahl: fragt per Section-Rendering-API die gefilterte
       Passende-Teile-Kollektion ab — kein statischer Index. */
    async fetchFitmentCount(gid) {
      if (!gid) return null;
      const base = (window.tsstec && window.tsstec.fitmentCollectionUrl) || '/collections/passende-teile';
      const url = `${base}?filter.p.m.custom.fits_vehicles=${encodeURIComponent(gid)}&section_id=fitment-count`;
      try {
        const r = await fetch(url, { credentials: 'same-origin' });
        if (!r.ok) return null;
        const html = await r.text();
        const m = html.match(/data-fitment-count="(\d+)"/);
        return m ? parseInt(m[1], 10) : null;
      } catch (e) { return null; }
    }

    fillSelect(select, values, placeholderText) {
      const placeholder = select.firstElementChild;
      if (placeholder && placeholderText) placeholder.textContent = placeholderText;
      const opts = placeholder ? [placeholder] : [];
      values.forEach(({ value, label }) => {
        const o = document.createElement('option');
        o.value = value; o.textContent = label;
        opts.push(o);
      });
      select.replaceChildren(...opts);
      select.disabled = values.length === 0;
    }

    populateBrands() {
      this.fillSelect(this.brandSelect, this.brands.map((b) => ({ value: b, label: formatBrand(b) })), 'Hersteller wählen');
      this.brandSelect.disabled = false;
    }

    populateDisplacements(brand) {
      if (!this.dispSelect) {
        // Karte ohne Hubraum-Feld: einfach Modelle direkt zeigen
        this.populateModels(brand, null);
        return;
      }
      const set = new Set(this.vehicles.filter((v) => v.brand === brand && v.displacement).map((v) => v.displacement));
      const list = [...set].sort((a, b) => displacementOrder(a) - displacementOrder(b))
        .map((d) => ({ value: d, label: `${d} ccm` }));
      this.fillSelect(this.dispSelect, list, 'Hubraum wählen');
      this.resetModel();
      this.resetYear();
    }

    populateModels(brand, displacement) {
      const filtered = this.vehicles.filter((v) => v.brand === brand && (!displacement || v.displacement === displacement));
      const models = [...new Set(filtered.map((v) => v.model))].sort((a, b) => a.localeCompare(b, 'de'));
      this.fillSelect(this.modelSelect, models.map((m) => ({ value: m, label: m })), 'Modell wählen');
      this.resetYear();
    }

    populateYears(brand, displacement, model) {
      const matches = this.vehicles
        .filter((v) => v.brand === brand && (!displacement || v.displacement === displacement) && v.model === model)
        .sort((a, b) => a.year_from - b.year_from);
      const opts = matches.map((v) => {
        const range = v.year_to && v.year_to !== v.year_from ? `${v.year_from}–${v.year_to}` : `${v.year_from}`;
        return { value: v.handle, label: range };
      });
      this.fillSelect(this.yearSelect, opts, 'Baujahr wählen');
    }

    resetModel() {
      if (!this.modelSelect) return;
      const ph = this.modelSelect.firstElementChild;
      this.modelSelect.replaceChildren(ph);
      this.modelSelect.disabled = true;
    }

    resetYear() {
      if (!this.yearSelect) return;
      const ph = this.yearSelect.firstElementChild;
      this.yearSelect.replaceChildren(ph);
      this.yearSelect.disabled = true;
      this.updateSubmitCount(null);
    }

    attachListeners() {
      this.brandSelect.addEventListener('change', () => {
        if (this.brandSelect.value) {
          this.populateDisplacements(this.brandSelect.value);
        } else {
          if (this.dispSelect) {
            this.dispSelect.replaceChildren(this.dispSelect.firstElementChild);
            this.dispSelect.disabled = true;
          }
          this.resetModel();
          this.resetYear();
        }
        this.renderChips();
        this.setStatus('');
      });

      if (this.dispSelect) {
        this.dispSelect.addEventListener('change', () => {
          if (this.dispSelect.value && this.brandSelect.value) {
            this.populateModels(this.brandSelect.value, this.dispSelect.value);
          } else {
            this.resetModel();
            this.resetYear();
          }
          this.renderChips();
          this.setStatus('');
        });
      }

      this.modelSelect.addEventListener('change', () => {
        if (this.modelSelect.value && this.brandSelect.value) {
          this.populateYears(this.brandSelect.value, this.dispSelect ? this.dispSelect.value : null, this.modelSelect.value);
        } else {
          this.resetYear();
        }
        this.renderChips();
        this.setStatus('');
      });

      this.yearSelect.addEventListener('change', async () => {
        this.renderChips();
        this.setStatus('');
        if (this.yearSelect.value) {
          const vehicle = this.vehicles.find((v) => v.handle === this.yearSelect.value);
          this.updateSubmitCount(null);
          const token = this.yearSelect.value;
          this._countToken = token;
          const count = await this.fetchFitmentCount(vehicle && vehicle.gid);
          if (this._countToken === token) this.updateSubmitCount(count);
        } else {
          this.updateSubmitCount(null);
        }
      });

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit();
      });

      if (this.resetButton) {
        this.resetButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.reset();
        });
      }

      // Wird das Fahrzeug woanders entfernt (z. B. "Filter entfernen" in der
      // Leiste unter dem Header), muss der Finder seine Auswahl mitleeren —
      // sonst stehen Marke/Modell/Baujahr noch drin, obwohl nichts mehr aktiv ist.
      document.addEventListener('tsstec:vehicle:cleared', (e) => {
        if (e.detail && e.detail.source === this) return;
        this.clearUi();
      });
    }

    updateSubmitCount(count) {
      if (!this.submitButton) return;
      if (typeof count === 'number') {
        this.submitButton.textContent = `${this.submitLabel} (${count})`;
        this.submitButton.disabled = count === 0;
      } else {
        this.submitButton.textContent = this.submitLabel;
        this.submitButton.disabled = false;
      }
    }

    renderChips() {
      if (!this.chipsBox) return;
      const items = [];
      if (this.brandSelect.value) items.push({ step: 'brand', label: formatBrand(this.brandSelect.value) });
      if (this.dispSelect && this.dispSelect.value) items.push({ step: 'displacement', label: `${this.dispSelect.value} ccm` });
      if (this.modelSelect.value) items.push({ step: 'model', label: this.modelSelect.value });
      if (this.yearSelect.value) {
        const yearText = this.yearSelect.options[this.yearSelect.selectedIndex]?.textContent || '';
        items.push({ step: 'year', label: yearText });
      }
      // Welche Steps waren vorher schon als Chip da? → nur neue animieren
      const prevSteps = new Set(
        [...this.chipsBox.querySelectorAll('[data-step]')].map((b) => b.dataset.step)
      );
      // Höhe vor dem Umbau merken (für sanfte Höhen-Animation)
      const oldHeight = this.chipsBox.getBoundingClientRect().height;

      this.chipsBox.innerHTML = items.map((it) =>
        `<button type="button" class="fitment-chip" data-step="${it.step}"><span>${escapeHtml(it.label)}</span><span class="fitment-chip__x" aria-hidden="true">✕</span></button>`
      ).join('');
      this.chipsBox.querySelectorAll('[data-step]').forEach((btn) => {
        if (!prevSteps.has(btn.dataset.step)) {
          btn.classList.add('fitment-chip--enter');
        }
        btn.addEventListener('click', () => {
          if (btn.classList.contains('fitment-chip--leave')) return;
          // Chip raus-animieren, dann erst entfernen
          btn.classList.remove('fitment-chip--enter');
          btn.classList.add('fitment-chip--leave');
          const step = btn.dataset.step;
          setTimeout(() => this.removeStep(step), 190);
        });
      });

      this.animateChipsHeight(oldHeight);
    }

    /* Animiert die Höhe der Chip-Reihe sanft von alt → neu (FLIP-Technik).
       Dadurch wächst/schrumpft auch die Karte und der Hero weich mit. */
    animateChipsHeight(oldHeight) {
      const box = this.chipsBox;
      if (!box) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // :empty { display:none } überschreiben, damit auch der Kollaps auf 0 animiert
      box.style.display = 'flex';
      const newHeight = box.getBoundingClientRect().height;
      if (Math.abs(oldHeight - newHeight) < 1) { box.style.display = ''; return; }
      box.style.height = oldHeight + 'px';
      box.style.overflow = 'hidden';
      box.getBoundingClientRect(); // Reflow erzwingen
      box.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      box.style.height = newHeight + 'px';
      clearTimeout(this._chipsHeightTimer);
      this._chipsHeightTimer = setTimeout(() => {
        box.style.height = '';
        box.style.transition = '';
        box.style.overflow = '';
        box.style.display = '';
      }, 340);
    }

    removeStep(step) {
      // Eine Chip-Entfernung macht die gespeicherte Auswahl unvollständig
      // -> Storage leeren, Reset-Button verbergen, Storefront benachrichtigen.
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      if (this.resetButton) this.resetButton.hidden = true;
      document.dispatchEvent(new CustomEvent('tsstec:vehicle:cleared', { detail: { source: this } }));

      if (step === 'brand') {
        this.brandSelect.value = '';
        this.brandSelect.dispatchEvent(new Event('change'));
        return;
      }
      if (step === 'displacement' && this.dispSelect) {
        this.dispSelect.value = '';
        this.dispSelect.dispatchEvent(new Event('change'));
        return;
      }
      if (step === 'model') {
        this.modelSelect.value = '';
        this.modelSelect.dispatchEvent(new Event('change'));
        return;
      }
      if (step === 'year') {
        this.yearSelect.value = '';
        this.yearSelect.dispatchEvent(new Event('change'));
      }
    }

    submit() {
      const handle = this.yearSelect.value;
      if (!handle) {
        this.setStatus('Bitte Hersteller, Modell und Baujahr auswählen.', 'error');
        return;
      }
      const vehicle = this.vehicles.find((v) => v.handle === handle);
      if (!vehicle) return;

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
      } catch (e) {
        console.warn('[fitment-search] localStorage nicht verfügbar', e);
      }

      document.dispatchEvent(new CustomEvent('tsstec:vehicle:changed', { detail: vehicle }));
      const base = (window.tsstec && window.tsstec.fitmentCollectionUrl) || '/collections/passende-teile';
      // Shopify-Metaobjekt-Filter erwartet die GID als Wert, nicht den Handle.
      window.location.href = `${base}?filter.p.m.custom.fits_vehicles=${encodeURIComponent(vehicle.gid)}`;
    }

    // Nur die Oberfläche leeren — ohne Storage anzufassen und ohne Event.
    // So kann der Finder auch auf ein Clearing von außen reagieren, ohne
    // dass sich die beiden gegenseitig im Kreis benachrichtigen.
    clearUi() {
      this.brandSelect.value = '';
      if (this.dispSelect) {
        this.dispSelect.replaceChildren(this.dispSelect.firstElementChild);
        this.dispSelect.disabled = true;
      }
      this.resetModel();
      this.resetYear();
      this.renderChips();
      this.updateSubmitCount(null);
      if (this.resetButton) this.resetButton.hidden = true;
      this.setStatus('');
    }

    reset() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      this.clearUi();
      document.dispatchEvent(new CustomEvent('tsstec:vehicle:cleared', { detail: { source: this } }));
    }

    async restoreFromStorage() {
      let stored = null;
      try { stored = localStorage.getItem(STORAGE_KEY); } catch (e) {}
      if (!stored) return;
      try {
        const v = JSON.parse(stored);
        if (!v || !v.brand) return;
        this.brandSelect.value = v.brand;
        if (this.dispSelect && v.displacement) {
          this.populateDisplacements(v.brand);
          this.dispSelect.value = v.displacement;
          this.populateModels(v.brand, v.displacement);
        } else {
          this.populateModels(v.brand, null);
        }
        this.modelSelect.value = v.model;
        this.populateYears(v.brand, this.dispSelect ? this.dispSelect.value : null, v.model);
        this.yearSelect.value = v.handle;
        if (this.resetButton) this.resetButton.hidden = false;
        const count = await this.fetchFitmentCount(v.gid);
        if (count != null) this.updateSubmitCount(count);
      } catch (e) {
        console.warn('[fitment-search] localStorage-Parsing fehlgeschlagen', e);
      }
    }

    setStatus(text, state) {
      if (!this.statusBox) return;
      this.statusBox.textContent = text || '';
      if (state) this.statusBox.dataset.state = state;
      else delete this.statusBox.dataset.state;
    }
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  if (!customElements.get('fitment-search')) {
    customElements.define('fitment-search', FitmentSearch);
  }
})();
